import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

type AlertRow = {
  id: string; kind: 'no_show' | 'reschedule' | 'urgent_message';
  inspector_name: string | null; inspector_user_id: string | null; conversation_id: string | null; claim_id: string | null;
  homeowner_name: string | null; claim_number: string | null;
  message: string; created_at: string;
};

/**
 * Banner ROJO full-width arriba de TODA la app (feedback 2026-07-30): alertas de inspector
 * (no-show del adjuster, reschedule de campo, mensaje urgente) para el equipo de Scheduling.
 * Aparece en cualquier sección y se queda hasta que CADA usuario le dé ✕ (dismissal por
 * usuario). El backend devuelve [] para quien no es de Scheduling → ni se renderiza.
 */
export function InspectorAlertBanner() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState<AlertRow[]>([]);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get<{ alerts: AlertRow[] }>('/api/inspector-alerts/active')
        .then((r) => { if (alive) setAlerts(r.alerts); })
        .catch(() => { /* best-effort */ });
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 30_000);
    const alVolver = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => { alive = false; clearInterval(iv); document.removeEventListener('visibilitychange', alVolver); };
  }, []);

  if (!alerts.length) return null;

  async function dismiss(id: string) {
    setAlerts((a) => a.filter((x) => x.id !== id)); // optimista
    try { await api.post(`/api/inspector-alerts/${id}/dismiss`, {}); } catch { /* reaparece en el próximo poll */ }
  }

  // "Ya le respondí" (2026-08-11): apaga la alerta para TODO el mundo (no solo para mí) y
  // deja la huella de quién respondió en los updates de Scheduling del claim.
  async function answered(id: string) {
    setAlerts((a) => a.filter((x) => x.id !== id)); // optimista
    try { await api.post(`/api/inspector-alerts/${id}/answer`, {}); } catch { /* reaparece en el próximo poll */ }
  }

  return (
    <div className="insp-alert-banner" role="alert">
      {alerts.map((a) => (
        <div key={a.id} className="insp-alert-row">
          <span className="insp-alert-tag">{t('ia.tag')}</span>
          <span className="insp-alert-msg">
            {/* El MENSAJE lleva al chat del inspector (su grupo con Scheduling) para responderle o ver
                si ya le respondieron (pedido del dueño 2026-09-10). Sin grupo, al DM con él. */}
            {a.inspector_user_id
              ? <Link to={`/messaging?inspector=${a.inspector_user_id}`}
                  style={{ color: '#fff', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>
                  {a.message}
                </Link>
              : a.message}
            {a.claim_id && (
              <>
                {' · '}
                {/* Directo al tab de SCHEDULING: sin ?tab abría el tab del depto de quien mira
                    (a Payments le abría Finance — pedido de Manuel 2026-08-11). */}
                <Link to={`/claims/${a.claim_id}?tab=scheduling`} style={{ color: '#fff', textDecoration: 'underline', fontWeight: 700 }}>
                  {a.homeowner_name || a.claim_number || `#${a.claim_id}`} ↗
                </Link>
              </>
            )}
            <span className="insp-alert-when">{new Date(a.created_at).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
          </span>
          {/* Respondida = se apaga para TODOS; Dismiss sigue siendo "quítamela a MÍ". */}
          <button className="insp-alert-x" onClick={() => answered(a.id)} title={t('ia.answeredHint')} style={{ background: 'rgba(255,255,255,0.18)' }}>
            <Check size={12} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {t('ia.answered')}
          </button>
          <button className="insp-alert-x" onClick={() => dismiss(a.id)} title={t('ia.dismiss')}><X size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ia.dismiss')}</button>
        </div>
      ))}
    </div>
  );
}
